import Service from '../../../Service';
import LoggerService from '../../LoggerService';
import type IAttentiveAddToCartEventPayload from './models/IAttentiveAddToCartEventPayload';
import type IAttentiveProductViewEventPayload from './models/IAttentiveProductViewEventPayload';
import type IAttentivePurchaseEventPayload from './models/IAttentivePurchaseEventPayload';
import type IAttentiveService from './IAttentiveService';

/**
 * Service for handling Attentive Tag SDK event firing.
 *
 * Sends product view, add-to-cart, and purchase events to window.attentive.analytics.
 * All methods include SDK guards to prevent errors when the Attentive script is not loaded.
 *
 * The Attentive script needs to be loaded on the page for the SDK to be available.
 */
export class AttentiveService extends Service implements IAttentiveService {
  /** @inheritdoc */
  public sendProductView(payload: IAttentiveProductViewEventPayload): void {
    return this.execGuardedAttentive('sendProductView', () => {
      window.attentive!.analytics!.productView(payload);
    });
  }

  /** @inheritdoc */
  public sendAddToCart(payload: IAttentiveAddToCartEventPayload): void {
    return this.execGuardedAttentive('sendAddToCart', () => {
      window.attentive!.analytics!.addToCart(payload);
    });
  }

  /** @inheritdoc */
  public sendPurchase(payload: IAttentivePurchaseEventPayload): void {
    return this.execGuardedAttentive('sendPurchase', () => {
      window.attentive!.analytics!.purchase(payload);
    });
  }

  /**
   * Checks if the Attentive SDK is available on the window object.
   *
   * @returns True if window.attentive.analytics is available, false otherwise.
   */
  private isAttentiveAvailable(): boolean {
    return (
      typeof window !== 'undefined' && window.attentive?.analytics !== undefined
    );
  }

  /**
   * Logs a warning when the Attentive SDK is unavailable.
   *
   * This is expected behavior when:
   * - The Attentive script hasn't loaded yet (lazy loading)
   * - The user has blocked third-party scripts
   * - The AttentiveMarketing component is not present on the page.
   *
   * @param methodName - The name of the method that was called.
   * @param callback - The callback to execute if the Attentive SDK is available.
   * @returns The result of the callback if the Attentive SDK is available, undefined otherwise.
   */
  private execGuardedAttentive(methodName: string, callback: () => void): void {
    if (this.isAttentiveAvailable()) {
      return callback();
    }

    LoggerService.warn(
      `Attentive SDK not available. Skipping ${methodName}. ` +
        `This is expected if the Attentive script hasn't loaded yet or is blocked.`
    );

    return undefined;
  }
}

export default new AttentiveService();
